import { zodResolver } from "@hookform/resolvers/zod"
import { format } from "date-fns"
import type { LucideIcon } from "lucide-react"
import {
  ArrowUpCircleIcon,
  CheckCircleIcon,
  CircleIcon,
  Clock3Icon,
  RotateCcwIcon,
  SearchIcon,
  Trash2Icon,
  XCircleIcon,
} from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import {
  useNavigate,
  useSearchParams,
} from "react-router-dom"

import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  SalesRequestTypeEnum,
  SalesStatusEnum,
} from "@/enums"
import { useSalesSearchParams } from "@/hooks"
import { toast } from "@/hooks/use-toast"
import { cn } from "@/lib/utils"
import type { FilterSalesFormSchemaProps } from "@/schemas/filter-sales"
import { filterSalesFormSchema } from "@/schemas/filter-sales"
import { DateHelper } from "@/utils"

import { AppDateInput } from "../commons"
interface StatusOption {
  value: SalesStatusEnum | "ALL"
  label: string
  icon: LucideIcon
}
interface RequestTypeOption {
  value: SalesRequestTypeEnum | "ALL"
  label: string
  icon: LucideIcon
}
export const SalesFilterForm = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { params, startDate, endDate } =
    useSalesSearchParams()
  const { t } = useTranslation()
  const statusOptions = useMemo<StatusOption[]>(
    () => [
      {
        value: "ALL",
        label: t("common.all"),
        icon: CircleIcon,
      },
      {
        value: SalesStatusEnum.AUTHORIZED,
        label: t("sales.status.AUTHORIZED"),
        icon: CheckCircleIcon,
      },
      {
        value: SalesStatusEnum.CANCELLED,
        label: t("sales.status.CANCELLED"),
        icon: XCircleIcon,
      },
      {
        value: SalesStatusEnum.FUTURE,
        label: t("sales.status.FUTURE"),
        icon: ArrowUpCircleIcon,
      },
      {
        value: SalesStatusEnum.PAYMENT_ERROR,
        label: t("sales.status.PAYMENT_ERROR"),
        icon: XCircleIcon,
      },
    ],
    [t]
  )
  const requestTypeOptions = useMemo<RequestTypeOption[]>(
    () => [
      {
        value: "ALL",
        label: t("common.all"),
        icon: CircleIcon,
      },
      {
        value: SalesRequestTypeEnum.RECURRING,
        label: t("sales.requestType.RECURRING"),
        icon: Clock3Icon,
      },
      {
        value: SalesRequestTypeEnum.ROTARY,
        label: t("sales.requestType.ROTARY"),
        icon: RotateCcwIcon,
      },
    ],
    [t]
  )
  const [statusSelected, setStatusSelected] = useState<
    StatusOption | undefined
  >(
    statusOptions.find(
      (status) => status.value === params.status
    )
  )
  const [requestTypeSelected, setRequestTypeSelected] =
    useState<RequestTypeOption | undefined>(
      requestTypeOptions.find(
        (requestType) =>
          requestType.value === params?.requestType
      ) || requestTypeOptions[0]
    )
  const defaultValues = useMemo(
    () => ({
      startDate: new Date(
        DateHelper.formatToISO(params.startDate)
      ),
      endDate: new Date(
        DateHelper.formatToISO(params.endDate)
      ),
      order_code: params?.order_code,
      status: params?.status,
      requestType: params?.requestType,
      proposalNumber: params?.proposalNumber,
    }),
    [
      params.startDate,
      params.endDate,
      params.order_code,
      params.status,
      params.requestType,
      params.proposalNumber,
    ]
  )
  const salesSchema = useMemo(
    () => filterSalesFormSchema(t),
    [t]
  )
  const form = useForm<FilterSalesFormSchemaProps>({
    resolver: zodResolver(salesSchema),
    defaultValues,
  })
  useEffect(() => {
    form.reset(defaultValues)
  }, [defaultValues])
  const hasFilter = useMemo(() => {
    return (
      !!params?.order_code ||
      !!params?.status ||
      !!params?.requestType ||
      !!params?.proposalNumber
    )
  }, [params])

  const handleSubmit = form.handleSubmit((data) => {
    if (data.startDate > data.endDate) {
      toast({
        title: t("validation.startDateGreaterThanEndDate"),
      })
      return
    }
    const params = new URLSearchParams()
    const pageSize = searchParams.get("pageSize")
    if (pageSize) params.set("pageSize", pageSize)
    Object.entries(data).forEach(([key, value]) => {
      if (
        (key === "requestType" && value === "ALL") ||
        (key === "status" && value === "ALL")
      ) {
        return params.delete(key)
      }
      if (value) {
        if (value instanceof Date) {
          value = format(value, "yyyy-MM-dd")
        }
        params.set(key, value)
      } else {
        params.delete(key)
      }
      params.set("refetch", "true")
    })
    navigate(`?${params.toString()}`)
  })
  const handleResetForm = () => {
    form.reset({
      order_code: "",
      status: "",
      startDate,
      endDate,
      requestType: "",
      proposalNumber: "",
    })
    setStatusSelected(statusOptions[0])
    setRequestTypeSelected(requestTypeOptions[0])
    const params = new URLSearchParams()
    Object.entries(params).forEach(([key]) => {
      if (params.get(key)) {
        params.delete(key)
      }
    })
    navigate(`?${params.toString()}`)
  }
  const formFieldEndDate = form.getValues("endDate")
  return (
    <div className="@container">
      <Form {...form}>
        <form
          onSubmit={handleSubmit}
          className="grid gap-4 @4xl:grid-cols-[1fr,auto]"
        >
          <div className="grid gap-4 @xl:grid-cols-2 @3xl:grid-cols-3">
            <FormField
              control={form.control}
              name="order_code"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormControl>
                    <Input
                      placeholder={t(
                        "sales.filters.searchByOrder"
                      )}
                      {...field}
                      className="w-full group-data-[sidebar-open=false]:2xl:min-w-[300px]"
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="startDate"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormControl>
                    <AppDateInput
                      onSelect={field.onChange}
                      selected={field.value}
                      placeholder={t("filters.startDate")}
                      toDate={formFieldEndDate}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="endDate"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormControl>
                    <AppDateInput
                      onSelect={field.onChange}
                      selected={field.value}
                      placeholder={t("filters.endDate")}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="status"
              defaultValue={params?.status}
              render={({ field }) => (
                <FormItem>
                  <Popover>
                    <FormControl>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="h-10 w-full justify-start"
                        >
                          {field.value && statusSelected ? (
                            <>
                              <statusSelected.icon className="mr-2 h-4 w-4" />
                              {statusSelected?.label}
                            </>
                          ) : (
                            <>
                              {t(
                                "orders.filters.selectStatus"
                              )}
                            </>
                          )}
                        </Button>
                      </PopoverTrigger>
                    </FormControl>
                    <PopoverContent
                      className="p-0"
                      align="end"
                    >
                      <Command>
                        <CommandInput
                          placeholder={t(
                            "orders.filters.searchStatus"
                          )}
                        />
                        <CommandList>
                          <CommandEmpty>
                            {t(
                              "orders.filters.noStatusFound"
                            )}
                          </CommandEmpty>
                          <CommandGroup>
                            {statusOptions.map((status) => (
                              <CommandItem
                                key={status.label}
                                value={status.label}
                                onSelect={() => {
                                  form.setValue(
                                    "status",
                                    status.value
                                  )
                                  setStatusSelected(status)
                                }}
                              >
                                <status.icon
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    status.value ===
                                      field?.value
                                      ? "opacity-100"
                                      : "opacity-40"
                                  )}
                                />
                                <span>{status.label}</span>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="requestType"
              defaultValue={params?.requestType}
              render={({ field }) => (
                <FormItem>
                  <Popover>
                    <FormControl>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="h-10 w-full justify-start"
                        >
                          {field.value &&
                          requestTypeSelected ? (
                            <>
                              <requestTypeSelected.icon className="mr-2 h-4 w-4" />
                              {requestTypeSelected?.label}
                            </>
                          ) : (
                            <>
                              {t(
                                "sales.filters.selectRequestType"
                              )}
                            </>
                          )}
                        </Button>
                      </PopoverTrigger>
                    </FormControl>
                    <PopoverContent
                      className="p-0"
                      align="start"
                    >
                      <Command>
                        <CommandInput
                          placeholder={t(
                            "sales.filters.searchRequestType"
                          )}
                        />
                        <CommandList>
                          <CommandEmpty>
                            {t("table.noResults")}
                          </CommandEmpty>
                          <CommandGroup>
                            {requestTypeOptions.map(
                              (requestType) => (
                                <CommandItem
                                  key={requestType.label}
                                  value={requestType.label}
                                  onSelect={() => {
                                    form.setValue(
                                      "requestType",
                                      requestType.value
                                    )
                                    setRequestTypeSelected(
                                      requestType
                                    )
                                  }}
                                >
                                  <requestType.icon
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      requestType.value ===
                                        field?.value
                                        ? "opacity-100"
                                        : "opacity-40"
                                    )}
                                  />
                                  <span>
                                    {requestType.label}
                                  </span>
                                </CommandItem>
                              )
                            )}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="proposalNumber"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormControl>
                    <Input
                      placeholder={t(
                        "sales.filters.searchByCustomerId"
                      )}
                      {...field}
                      className="w-full"
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
          <div className="flex w-full flex-col justify-start gap-4 @xl:flex-row-reverse @4xl:flex-col">
            <Button
              type="submit"
              className="btn-whitelabel"
            >
              <SearchIcon size={18} className="mr-2" />
              {t("common.search")}
            </Button>
            {hasFilter && (
              <Button
                type="button"
                variant="outline"
                onClick={handleResetForm}
              >
                <Trash2Icon size={18} className="mr-2" />
                {t("common.clear")}
              </Button>
            )}
          </div>
        </form>
      </Form>
    </div>
  )
}
